import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useGroupLabels from 'hooks/useGroupLabels';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import styles from './EventPopup.module.css';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import { getEventById, updateEvent, addEvent } from 'api/eventApi';
import { getClubEventTypes } from 'api/club/eventTypeApi';
import { getClubAgeGroups } from 'api/club/ageGroupApi';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import ReplayDelayNote from 'components/admin/ReplayDelayNote';
import { clubForId, organisationWordOf } from 'utils/clubFeatures';
import { eventTypeOptionsFor } from 'utils/eventTypeOptions';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const roundTypeOptions = ['Heat', 'Semi', 'Final'].map(
  (item) => ({
    label: item,
    value: item,
  })
);

const addModeRoundTypeOptions = ['Heat', 'Semi', 'Final', 'Training'].map(
  (item) => ({
    label: item,
    value: item,
  })
);

const surfaceTypeOptions = ['Synthetic', 'Grass'].map(
  (item) => ({
    label: item,
    value: item,
  })
);

const genderOptions = ['Male', 'Female', 'Mixed'].map(
  (item) => ({
    label: item,
    value: item === 'Mixed' ? item : item.charAt(0),
  })
);

// Field events that require track surface (excludes Discus 64 and Shot Put 66)
const FIELD_EVENTS_WITH_SURFACE = [61, 62, 63, 65]; // High Jump, Long Jump, Triple Jump, Javelin

/**
 * EventPopup - Combined Add/Edit Event Popup
 *
 * For ADD mode, pass: mode="add", clubID, meetID, showPopup, closePopup, onSuccess
 * For EDIT mode, pass: mode="edit", eventID, clubID, meetID, showPopup, closePopup, onSuccess (optional)
 */
const EventPopup = ({
  mode = 'edit',
  eventID,
  clubID,
  meetID,
  showPopup,
  closePopup,
  onSuccess
}) => {
  // "Age Group", or "Year Group" for a club that groups by year level.
  const { words } = useGroupLabels(clubID);
  // "club records", or "school records" for a school, in the note under the form.
  const organisationWord = useSelector((state) => organisationWordOf(clubForId(state.user, clubID)));
  const [isLoading, setIsLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [ageGroups, setAgeGroups] = useState([]);

  const [formData, setFormData] = useState({
    eventTypeCode: 0,
    roundType: '',
    ageGroup: '',
    gender: '',
    surfaceType: '',
  });

  const [errors, setErrors] = useState({});
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const initialFormData = useRef(null);

  const isAddMode = mode === 'add';

  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!initialFormData.current) return false;
    return JSON.stringify(formData) !== JSON.stringify(initialFormData.current);
  }, [formData]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      // In add mode, when event type changes, reset surfaceType if not applicable
      if (isAddMode && name === 'eventTypeCode') {
        const selectedEventType = eventTypes.find(et => et.eventCode === value);
        const needsSurface = selectedEventType && (
          selectedEventType.category === 'Track' ||
          (selectedEventType.category === 'Field' && FIELD_EVENTS_WITH_SURFACE.includes(value))
        );
        if (!needsSurface) {
          newData.surfaceType = '';
        }
      }

      return newData;
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Get category of selected event type (for add mode)
  const selectedEventTypeData = useMemo(() => {
    if (formData.eventTypeCode) {
      return eventTypes.find(et => et.eventCode === formData.eventTypeCode);
    }
    return null;
  }, [formData.eventTypeCode, eventTypes]);

  // Determine if surface type should be shown
  const showSurfaceType = useMemo(() => {
    if (isAddMode) {
      if (!selectedEventTypeData) return false;
      return selectedEventTypeData.category === 'Track' ||
        (selectedEventTypeData.category === 'Field' && FIELD_EVENTS_WITH_SURFACE.includes(formData.eventTypeCode));
    } else {
      // Edit mode - use selectedEvent
      if (!selectedEvent) return false;
      return selectedEvent.eventCategory === 'Track' ||
        (selectedEvent.eventCategory === 'Field' && FIELD_EVENTS_WITH_SURFACE.includes(formData.eventTypeCode));
    }
  }, [isAddMode, selectedEvent, selectedEventTypeData, formData.eventTypeCode]);

  const validateForm = () => {
    const newErrors = {};

    if (formData.eventTypeCode === 0) {
      newErrors.eventTypeCode = 'The event type is required';
    }
    if (!formData.roundType.trim()) {
      newErrors.roundType = 'The round type is required';
    }
    if (!formData.ageGroup.trim()) {
      newErrors.ageGroup = `The ${words.lower.group} is required`;
    }
    if (!formData.gender) {
      newErrors.gender = 'The gender is required';
    }
    if (showSurfaceType && !formData.surfaceType) {
      newErrors.surfaceType = 'The surface type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Edit mode with nothing changed: close, and ask nothing of the server
    // (Doug, 2026-10-16: a Save with no change still updated the event).
    if (!isAddMode && !hasUnsavedChanges) {
      closePopup();
      return;
    }
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);

    const selectedEventType = eventTypeOptions.find(et => et.value === formData.eventTypeCode);

    try {
      const payload = {
        ...formData,
        meetID,
        clubID,
        eventType: selectedEventType ? selectedEventType.label : '',
      };

      // Only include surfaceType if applicable
      if (!showSurfaceType) {
        delete payload.surfaceType;
      }

      if (isAddMode) {
        // Add mode
        const addPayload = {
          clubID,
          meetID,
          eventType: payload.eventType,
          ageGroup: payload.ageGroup,
          gender: payload.gender,
          roundType: payload.roundType,
        };
        // Only include surfaceType for events that need it (not Shot Put or Discus)
        if (showSurfaceType && payload.surfaceType) {
          addPayload.surfaceType = payload.surfaceType;
        }
        await addEvent(addPayload);
        toast.success('Event created successfully');
      } else {
        // Edit mode
        payload.eventID = eventID;
        await updateEvent(payload);
        toast.success('Event updated successfully');
      }

      setIsLoading(false);
      if (onSuccess) {
        onSuccess();
      }
      closePopup();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data || `Failed to ${isAddMode ? 'create' : 'update'} event`);
    } finally {
      setIsLoading(false);
    }
  };

  const eventTypeOptions = useMemo(
    () => eventTypeOptionsFor({ eventTypes, isAddMode, selectedEvent }),
    [eventTypes, selectedEvent, isAddMode]
  );

  const fetchEvent = useCallback(async () => {
    if (!eventID || isAddMode) return;
    try {
      const response = await getEventById(eventID);
      setSelectedEvent(response.data);
    } catch (error) {
      console.error('Error fetching event data:', error);
    }
  }, [eventID, isAddMode]);

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await getClubEventTypes(clubID);
      setEventTypes(response.data);
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  }, [clubID]);

  const fetchAgeGroups = useCallback(async () => {
    try {
      const response = await getClubAgeGroups(clubID);
      if (response.data.length > 0) {
        const groups = response.data.filter(ag => ag.isVisible).map(ag => {
          return {
            label: ag.ageGroupName,
            value: ag.ageGroupName
          }
        });
        setAgeGroups([...groups, {
          label: "Mixed",
          value: "Mixed"
        }]);
      }
    } catch (error) {
      console.error('Error fetching age groups:', error);
    }
  }, [clubID]);

  useEffect(() => {
    if (showPopup) {
      fetchEventTypes();
      fetchAgeGroups();
      if (!isAddMode) {
        fetchEvent();
      } else {
        // Reset form for add mode
        const defaultFormData = {
          eventTypeCode: 0,
          roundType: '',
          ageGroup: '',
          gender: '',
          surfaceType: '',
        };
        setFormData(defaultFormData);
        initialFormData.current = defaultFormData;
        setErrors({});
        setSelectedEvent(null);
        setShowUnsavedModal(false);
      }
    }
  }, [showPopup, fetchEvent, fetchAgeGroups, fetchEventTypes, isAddMode]);

  useEffect(() => {
    // Only populate form with existing data in edit mode
    if (!isAddMode && selectedEvent && eventTypeOptions && eventTypeOptions.length > 0) {
      const initialData = {
        eventTypeCode: selectedEvent.eventTypeCode || 0,
        roundType: selectedEvent.roundType || '',
        ageGroup: selectedEvent.ageGroup || '',
        gender: selectedEvent.gender || '',
        surfaceType: selectedEvent.surfaceType || '',
      }

      if (selectedEvent.displayName === 'High Jump') {
        initialData.eventTypeCode = 61;
      }

      setFormData(initialData);
      initialFormData.current = initialData;
    }
  }, [selectedEvent, eventTypeOptions, isAddMode]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      closePopup();
    }
  }, [hasUnsavedChanges, closePopup]);

  // Confirm discard changes
  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    closePopup();
  };

  if (!showPopup) {
    return null;
  }

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">
              {isAddMode ? 'Add Event' : 'Edit Event'}
            </div>
            <div className={styles.form}>
              <div className={styles.twoColumnRow}>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label="Event Type"
                    name="eventTypeCode"
                    value={formData.eventTypeCode}
                    onChange={handleInputChange}
                    options={eventTypeOptions ?? []}
                    error={errors.eventTypeCode}
                    disabled={isLoading || (!isAddMode && selectedEvent && selectedEvent.eventType === 'High Jump')}
                    placeholder=""
                  />
                  {errors.eventTypeCode && (
                    <span className={styles.errorText}>{errors.eventTypeCode}</span>
                  )}
                </div>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label="Round Type"
                    name="roundType"
                    value={formData.roundType}
                    onChange={handleInputChange}
                    options={isAddMode ? addModeRoundTypeOptions : roundTypeOptions}
                    error={errors.roundType}
                    disabled={isLoading}
                    placeholder=""
                  />
                  {errors.roundType && (
                    <span className={styles.errorText}>
                      {errors.roundType}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.twoColumnRow}>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label={words.group}
                    name="ageGroup"
                    value={formData.ageGroup}
                    onChange={handleInputChange}
                    options={ageGroups}
                    error={errors.ageGroup}
                    disabled={isLoading}
                    placeholder=""
                  />
                  {errors.ageGroup && (
                    <span className={styles.errorText}>{errors.ageGroup}</span>
                  )}
                </div>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    options={genderOptions}
                    error={errors.gender}
                    disabled={isLoading}
                    placeholder=""
                  />
                  {errors.gender && (
                    <span className={styles.errorText}>
                      {errors.gender}
                    </span>
                  )}
                </div>
              </div>
              {/* No Round Number: a race's number is its place in its round in
                  time order, from when it was added here or run on an app
                  (portal repo, step 3 file, 12.12). */}
              {showSurfaceType && (
                <div className={styles.twoColumnRow}>
                  <div className={styles.flex1}>
                    <CustomDropdown
                      label="Track Surface"
                      name="surfaceType"
                      value={formData.surfaceType}
                      onChange={handleInputChange}
                      options={surfaceTypeOptions}
                      error={errors.surfaceType}
                      disabled={isLoading}
                      placeholder=""
                    />
                    {errors.surfaceType && (
                      <span className={styles.errorText}>{errors.surfaceType}</span>
                    )}
                  </div>
                  <div className={styles.flex1} />
                </div>
              )}
              {/* Inside the form so it follows the fields; the form's bottom padding (room for open
                  dropdowns) then sits between the note and Save. */}
              {!isAddMode && (
                <ReplayDelayNote
                  lead="Changing the event type moves this event's results to the new event type."
                  organisationWord={organisationWord}
                />
              )}
            </div>
            <div className={styles.buttons}>
              <CustomNewButton
                onClick={handleSave}
                label={isAddMode ? 'Add Event' : 'Save'}
                large={true}
                disabled={isLoading}
              />
            </div>
            <div className="goBack" onClick={handleClose}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={handleClose}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Confirmation Modal */}
      <CustomNewConfirm
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        title="Discard Changes?"
        description="You have unsaved changes. Leave this page?"
        onConfirm={handleDiscardChanges}
        confirmButtonText="OK"
        confirmButtonVariant="danger"
        onCancel={() => setShowUnsavedModal(false)}
      />
    </>
  );
}

EventPopup.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  eventID: PropTypes.string,
  clubID: PropTypes.string.isRequired,
  meetID: PropTypes.string.isRequired,
  showPopup: PropTypes.bool.isRequired,
  closePopup: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default EventPopup;
